import { useEffect } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || window.location.origin;
const SITE_URL = import.meta.env.VITE_SITE_URL || window.location.origin;
const SUPPORTED_LANGS = ['en', 'tr', 'ru', 'no'];

const setMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null) {
      el.removeAttribute(k);
    } else {
      el.setAttribute(k, v);
    }
  }
};

const upsertHreflangs = (pageOrPath) => {
  const path = pageOrPath?.startsWith('/')
    ? pageOrPath
    : pageOrPath === 'home'
      ? '/'
      : `/#${pageOrPath}`;
  document.head
    .querySelectorAll('link[rel="alternate"][data-managed="seo"]')
    .forEach((node) => node.parentNode.removeChild(node));
  for (const code of SUPPORTED_LANGS) {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', code);
    link.setAttribute('href', `${SITE_URL}${path}?lang=${code}`);
    link.setAttribute('data-managed', 'seo');
    document.head.appendChild(link);
  }
  const xDefault = document.createElement('link');
  xDefault.setAttribute('rel', 'alternate');
  xDefault.setAttribute('hreflang', 'x-default');
  xDefault.setAttribute('href', `${SITE_URL}${path}`);
  xDefault.setAttribute('data-managed', 'seo');
  document.head.appendChild(xDefault);
};

const upsertCanonical = (url) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
};

export default function useSeo(pageKey, lang = 'en', overrides = null) {
  const overrideKey = overrides
    ? `${overrides.title || ''}|${overrides.description || ''}|${overrides.canonical || ''}|${overrides.image || ''}|${overrides.path || ''}`
    : '';

  useEffect(() => {
    let cancelled = false;
    const apply = async () => {
      let data = {};
      try {
        const res = await axios.get(`${API_URL}/api/seo/meta`, {
          params: { page: pageKey, lang },
        });
        if (cancelled) return;
        data = res?.data?.data || {};
      } catch (err) {
        console.error('SEO load failed:', err);
      }

      const merged = {
        title: overrides?.title || data.title || '',
        description: overrides?.description || data.description || '',
        keywords: overrides?.keywords || data.keywords || '',
        og_image: overrides?.image || data.og_image || '',
        canonical_url:
          overrides?.canonical ||
          data.canonical_url ||
          `${SITE_URL}${overrides?.path || (pageKey === 'home' ? '/' : '/#' + pageKey)}`,
      };

      if (merged.title) document.title = merged.title;
      document.documentElement.lang = lang;

      setMeta('meta[name="description"]', { name: 'description', content: merged.description });
      setMeta('meta[name="keywords"]', { name: 'keywords', content: merged.keywords });

      setMeta('meta[property="og:title"]', { property: 'og:title', content: merged.title });
      setMeta('meta[property="og:description"]', { property: 'og:description', content: merged.description });
      setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
      setMeta('meta[property="og:locale"]', { property: 'og:locale', content: lang });
      setMeta('meta[property="og:url"]', { property: 'og:url', content: merged.canonical_url });
      if (merged.og_image) {
        setMeta('meta[property="og:image"]', { property: 'og:image', content: merged.og_image });
      }

      setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
      setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: merged.title });
      setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: merged.description });
      if (merged.og_image) {
        setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: merged.og_image });
      }

      upsertCanonical(merged.canonical_url);
      upsertHreflangs(overrides?.hreflangPath || pageKey);
    };
    apply();
    return () => {
      cancelled = true;
    };
  }, [pageKey, lang, overrideKey]);
}
